import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  BarChart3,
  BrainCircuit,
  ChevronRight,
  CircleDollarSign,
  FileClock,
  FlaskConical,
  Gauge,
  LayoutDashboard,
  LineChart,
  Newspaper,
  PanelLeftClose,
  PanelLeftOpen,
  Radio,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  TerminalSquare,
  WalletCards,
  Blocks,
  Globe,
} from "lucide-react";
import "@/App.css";
import { useControlRoomStream, useInfinitePoll, usePoll, post } from "@/lib/api";
import { getAccountRuntimeTruth } from "@/lib/accountRuntime";
import Header from "@/components/Header";
import PositionsTable from "@/components/PositionsTable";
import OpenOrdersTable from "@/components/OpenOrdersTable";
import TradesTable from "@/components/TradesTable";

import SignalsFeed from "@/components/SignalsFeed";
import RiskPanel from "@/components/RiskPanel";
import ScannerPanel from "@/components/ScannerPanel";
import LogsPanel from "@/components/LogsPanel";
import EquityChart from "@/components/EquityChart";
import AuthGate from "@/components/AuthGate";
import ErrorBoundary from "@/components/ErrorBoundary";
import MissionStrip from "@/components/MissionStrip";
import ExecutionPanel from "@/components/ExecutionPanel";
import AlertsPanel from "@/components/AlertsPanel";
import {
  CandlestickIcon,
  BrainCouncilIcon,
  ShieldGuardIcon,
  ActivityLogIcon,
  NewsFeedIcon,
  WalletCapitalIcon,
  TargetSignalIcon,
  FlaskLabIcon,
  TrendUpIcon,
} from "@/components/CyberIcons";
import { API } from "@/lib/api";
import { selectControlRoomData } from "./lib/controlRoomState";
import { isKnownView, pathForView, routeFromLocation } from "@/lib/routes";
import { ACCOUNT_POLL_INTERVALS, shouldPollViewData } from "@/lib/pollingPolicy";

const AIPanel = lazy(() => import("@/components/AIPanel"));
const AllLogsPanel = lazy(() => import("@/components/AllLogsPanel"));
const AllNewsPanel = lazy(() => import("@/components/AllNewsPanel"));
const BacktestLabPanel = lazy(() => import("@/components/BacktestLabPanel"));
const BacktestModal = lazy(() => import("@/components/BacktestModal"));
const BlockchainMonitor = lazy(() => import("@/components/BlockchainMonitor"));
const ChartWorkspace = lazy(() => import("@/components/ChartWorkspace"));
const AgentFabricPanel = lazy(() => import("@/components/AgentFabricPanel"));
const DailyPnlPanel = lazy(() => import("@/components/DailyPnlPanel"));
const ResearchTelemetry = lazy(() => import("@/components/ResearchTelemetry"));
const SettingsPanel = lazy(() => import("@/components/SettingsPanel"));
const MarketUniversePanel = lazy(() => import("@/components/MarketUniversePanel"));

const NAV_ITEMS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "market_universe", label: "Market Universe", icon: Globe },
  { id: "chart", label: "Chart", icon: CandlestickIcon },
  { id: "positions", label: "Positions", icon: WalletCapitalIcon },
  { id: "daily_pnl", label: "Daily PnL", icon: TrendUpIcon },
  { id: "signals", label: "Signals", icon: TargetSignalIcon },
  { id: "news", label: "All news", icon: NewsFeedIcon },
  { id: "agents", label: "All agents", icon: ShieldGuardIcon },
  { id: "backtest", label: "Backtest Lab", icon: FlaskLabIcon },
  { id: "research", label: "Research lab", icon: FlaskConical },
  { id: "ai", label: "AI council", icon: BrainCouncilIcon },
  { id: "activity", label: "Activity log", icon: ActivityLogIcon },
  { id: "all_logs", label: "All Logs", icon: TerminalSquare },
  { id: "blockchain", label: "Blockchain monitor", icon: Blocks },
];

function WorkspaceLoading() {
  return (
    <section className="panel panel-standalone" aria-live="polite" data-testid="workspace-module-loading">
      <div className="empty-state">
        <Activity size={18} />
        <div><strong>Opening workspace</strong><span>Loading this module without blocking account controls.</span></div>
      </div>
    </section>
  );
}


// Memoized: these four re-render on every App render otherwise, and App
// re-renders whenever any of a dozen polls lands. Their props are now stable
// (handlers via useCallback, poll payloads reference-preserved in api.js), so
// an unchanged poll tick costs nothing here.
const Sidebar = memo(function Sidebar({ activeView, onNavigate, collapsed, onToggle, status }) {
  const runtimeTruth = getAccountRuntimeTruth(status);
  return (
    <aside className={`app-sidebar ${collapsed ? "app-sidebar-collapsed" : ""}`}>
      <div className="brand-lockup">
        <div className="brand-mark"><Activity size={19} /></div>
        {!collapsed && (
          <div className="brand-copy">
            <strong>ATLAS</strong>
            <span>FUTURES CONTROL</span>
          </div>
        )}
      </div>

      <div className="sidebar-section-label">{!collapsed && "Workspace"}</div>
      <nav className="sidebar-nav" aria-label="Primary navigation">
        {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`sidebar-nav-item ${activeView === id ? "active" : ""}`}
            onClick={() => onNavigate(id)}
            title={collapsed ? label : undefined}
            data-testid={`nav-${id}`}
          >
            <Icon size={17} />
            {!collapsed && <span>{label}</span>}
            {!collapsed && activeView === id && <ChevronRight size={14} className="nav-chevron" />}
          </button>
        ))}
      </nav>

      {!collapsed && <div className="sidebar-section-label sidebar-section-label-spaced">Operations</div>}
      <nav className="sidebar-nav">
        <button className={`sidebar-nav-item ${activeView === "settings" ? "active" : ""}`} onClick={() => onNavigate("settings")} data-testid="nav-settings">
          <Settings2 size={17} />
          {!collapsed && <span>Settings</span>}
          {!collapsed && activeView === "settings" && <ChevronRight size={14} className="nav-chevron" />}
        </button>
      </nav>

      <div className="sidebar-spacer" />
      {!collapsed && (
        <div className="sidebar-health-card">
          <div className="sidebar-health-heading"><span className={`status-dot ${runtimeTruth.running ? "online" : "idle"}`} /> Selected account</div>
          <div className="sidebar-health-row"><span>Engine</span><strong className={runtimeTruth.running ? "text-success" : runtimeTruth.executionTone === "danger" ? "text-danger" : "text-muted"}>{runtimeTruth.executionLabel}</strong></div>
          <div className="sidebar-health-row"><span>Account</span><strong>{runtimeTruth.accountType.toUpperCase()}</strong></div>
          <div className="sidebar-health-row"><span>Risk guard</span><strong className={runtimeTruth.riskLocked ? "text-danger" : runtimeTruth.loaded ? "text-success" : "text-muted"}>{!runtimeTruth.loaded ? "CHECKING" : runtimeTruth.riskLocked ? "LOCKED" : "ARMED"}</strong></div>
        </div>
      )}
      <button className="sidebar-collapse-button" onClick={onToggle} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
        {collapsed ? <PanelLeftOpen size={17} /> : <><PanelLeftClose size={17} /><span>Collapse</span></>}
      </button>
    </aside>
  );
});

const PageHeader = memo(function PageHeader({ eyebrow, title, subtitle, action }) {
  return (
    <div className="page-heading">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p className="page-subtitle">{subtitle}</p>
      </div>
      {action && <div className="page-heading-actions">{action}</div>}
    </div>
  );
});

const StatStrip = memo(function StatStrip({ status, perf }) {
  const isPaper = status?.mode === "paper";
  const isLive = status?.mode === "live";
  const accountType = status?.exchange?.account_type || (isPaper ? "paper" : "demo");
  const isReal = isLive && accountType === "live";

  const netEquityBadge = isPaper ? "PAPER SIM" : isReal ? "LIVE REAL" : "LIVE DEMO";
  const netEquityDetail = isPaper
    ? "paper simulation account"
    : `${status?.exchange?.account_name || "Binance"} (${accountType} account)`;
  const openTrades = status?.risk?.open_trades;
  const maxConcurrentTrades = status?.risk?.max_concurrent_trades;
  const hasPositionCapacity = Number.isFinite(Number(openTrades))
    && Number.isFinite(Number(maxConcurrentTrades));

  const stats = [
    {
      label: "Net equity",
      value: status?.equity == null ? "—" : `$${Number(status.equity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      detail: netEquityDetail,
      icon: CircleDollarSign,
      tone: isPaper ? "blue" : isReal ? "green" : "violet",
      badge: netEquityBadge,
    },
    {
      label: "Session PnL",
      value: perf?.realized_pnl == null ? "—" : `${perf.realized_pnl >= 0 ? "+" : ""}$${Number(perf.realized_pnl).toFixed(2)}`,
      detail: `${perf?.total_trades || 0} closed trades`,
      icon: LineChart,
      tone: perf?.realized_pnl >= 0 ? "green" : "red",
      badge: perf?.realized_pnl >= 0 ? "GAIN" : "DRAWDOWN",
    },
    {
      label: "Win rate",
      value: perf?.win_rate == null ? "—" : `${Number(perf.win_rate).toFixed(1)}%`,
      detail: perf?.profit_factor ? `PF ${Number(perf.profit_factor).toFixed(2)}` : "awaiting data",
      icon: BarChart3,
      tone: "violet",
      badge: "CONFLUENCE",
    },
    {
      label: "Active exposure",
      value: status?.risk?.exposure == null ? "—" : `$${Number(status.risk.exposure).toFixed(0)}`,
      detail: hasPositionCapacity
        ? `${openTrades}/${maxConcurrentTrades} open positions`
        : "position capacity unavailable",
      icon: Gauge,
      tone: "blue",
      badge: "RISK",
    },
  ];
  return (
    <div className="stat-strip">
      {stats.map(({ label, value, detail, icon: Icon, tone, badge }) => (
        <div className={`stat-card stat-card-${tone} relative overflow-hidden`} key={label}>
          <div className="stat-card-top">
            <span className="uppercase tracking-wider text-[10px] font-bold text-zinc-400">{label}</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-zinc-900 border border-zinc-800 text-zinc-400 font-semibold">{badge}</span>
              <Icon size={16} />
            </div>
          </div>
          <strong className="text-xl font-bold font-mono text-zinc-100 my-1">{value}</strong>
          <small className="text-[10px] font-mono text-zinc-500">{detail}</small>
        </div>
      ))}
    </div>
  );
});

const CouncilPulse = memo(function CouncilPulse({ ai, status, onOpen }) {
  const lastVerdict = ai?.last_insight;
  const isArmed = ai?.enabled === true;
  const advisorTelemetry = ai?.telemetry?.advisors;
  const advisorCount = Array.isArray(advisorTelemetry) ? advisorTelemetry.length : null;
  const openPositions = status?.risk?.open_trades;
  const pendingOrders = status?.pending_orders;
  const hasExecutionCounts = Number.isFinite(Number(openPositions))
    && Number.isFinite(Number(pendingOrders));
  const advisorStatus = advisorCount == null
    ? "NOT REPORTED"
    : `${advisorCount} CONFIGURED`;
  const executionStatus = hasExecutionCounts
    ? `${openPositions} POS / ${pendingOrders} ORD`
    : "NOT REPORTED";

  return (
    <div className="council-pulse" data-testid="overview-council-pulse">
      <div className="council-pulse-heading">
        <div><span>INTELLIGENCE / AI COUNCIL</span><strong>Decision desk</strong></div>
        <b className={isArmed ? "armed" : "standby"}>{isArmed ? advisorStatus : "RULE-BASED"}</b>
      </div>
      <div className="council-pulse-body">
        <div className="council-pulse-verdict">
          <BrainCircuit size={18} />
          <div>
            <strong>{lastVerdict ? `${lastVerdict.symbol?.replace(":USDT", "")} · ${lastVerdict.side?.toUpperCase() || "REVIEW"}` : "No decision waiting"}</strong>
            <span>{lastVerdict ? `${Number.isFinite(Number(lastVerdict.confidence)) ? `${Math.round(Number(lastVerdict.confidence) * 100)}% confidence` : "confidence not reported"} · ${lastVerdict.regime || "regime not reported"}` : "No council verdict has been recorded."}</span>
          </div>
        </div>
        <div className="council-pulse-facts">
          <span><small>Advisor teams</small><b>{advisorStatus}</b></span>
          <span><small>Execution</small><b>{executionStatus}</b></span>
        </div>
      </div>
      <button type="button" onClick={onOpen}>Open Decision Control Room <ChevronRight size={14} /></button>
    </div>
  );
});

export default function App() {
  const [selectedViewAccountId, setSelectedViewAccountId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("view_account") || localStorage.getItem("selectedViewAccountId") || "paper";
  });

  const handleSelectViewAccount = useCallback(async (accId) => {
    const previousAccountId = localStorage.getItem("selectedViewAccountId") || "paper";
    setSelectedViewAccountId(accId);
    localStorage.setItem("selectedViewAccountId", accId);
    const url = new URL(window.location.href);
    url.searchParams.set("view_account", accId);
    window.history.replaceState({}, "", url.toString());
    try {
      // Every account-scoped poll path contains the account id, so switching
      // accounts already refetches them. No extra re-render nudge needed.
      await post("/bot/account/select", { account_id: accId });
    } catch (err) {
      setSelectedViewAccountId(previousAccountId);
      localStorage.setItem("selectedViewAccountId", previousAccountId);
      url.searchParams.set("view_account", previousAccountId);
      window.history.replaceState({}, "", url.toString());
      throw err;
    }
  }, []);

  const [activeView, setActiveView] = useState(() => routeFromLocation(window.location).view);
  const [showBacktest, setShowBacktest] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [secondaryPollsReady, setSecondaryPollsReady] = useState(false);
  const selectedAccountPathId = encodeURIComponent(selectedViewAccountId);
  const controlRoomStream = useControlRoomStream();
  const streamSnapshot = controlRoomStream.snapshots[selectedViewAccountId];
  const streamReady = Boolean(controlRoomStream.connected && streamSnapshot);

  // `interval` is a poll-effect dependency, so every flip tears down and
  // rebuilds the status/positions timers. Drop to the fast interval the moment
  // the stream dies, but only relax to the slow one after it has held for a
  // few seconds — that keeps a flapping stream from thrashing the polls.
  const [streamSettled, setStreamSettled] = useState(false);
  useEffect(() => {
    if (!streamReady) {
      setStreamSettled(false);
      return undefined;
    }
    const timer = setTimeout(() => setStreamSettled(true), 5000);
    return () => clearTimeout(timer);
  }, [streamReady]);

  const realtimeFallbackInterval = streamSettled
    ? ACCOUNT_POLL_INTERVALS.streamBackup
    : ACCOUNT_POLL_INTERVALS.fallbackRealtime;
  const viewPollEnabled = (group) => secondaryPollsReady && shouldPollViewData(group, activeView);
  const accountsQuery = usePoll("/accounts", 30000, []);
  const statusQuery = usePoll(
    `/accounts/${selectedAccountPathId}/status`,
    realtimeFallbackInterval,
  );
  const positionsQuery = useInfinitePoll(
    `/accounts/${selectedAccountPathId}/positions`,
    realtimeFallbackInterval,
    [],
    50,
    { enabled: viewPollEnabled("positions") },
  );
  const signalsQuery = useInfinitePoll(
    `/accounts/${selectedAccountPathId}/signals`,
    ACCOUNT_POLL_INTERVALS.signals,
    [],
    50,
    { enabled: viewPollEnabled("signals") },
  );
  const tradesQuery = useInfinitePoll(
    `/accounts/${selectedAccountPathId}/trades`,
    ACCOUNT_POLL_INTERVALS.trades,
    [],
    50,
    { enabled: viewPollEnabled("trades") },
  );
  const logsQuery = useInfinitePoll(
    "/logs",
    ACCOUNT_POLL_INTERVALS.logs,
    [],
    100,
    { prependOnLoadMore: true, enabled: viewPollEnabled("logs") },
  );
  const perfQuery = usePoll(
    `/accounts/${selectedAccountPathId}/performance`,
    ACCOUNT_POLL_INTERVALS.performance,
    null,
    { enabled: viewPollEnabled("performance") },
  );
  const portfolioQuery = usePoll("/portfolio/analytics", 30000, {}, { enabled: viewPollEnabled("research") });
  const reportQuery = usePoll("/reports/summary", 60000, {}, { enabled: viewPollEnabled("research") });
  const validationQuery = usePoll("/validation/report", 60000, {}, { enabled: viewPollEnabled("research") });
  const modelQuery = usePoll("/model/status", 60000, {}, { enabled: viewPollEnabled("research") });
  const ordersQuery = useInfinitePoll(
    `/accounts/${selectedAccountPathId}/orders`,
    ACCOUNT_POLL_INTERVALS.orders,
    [],
    50,
    { enabled: viewPollEnabled("orders") },
  );


  const {
    status,
    positions,
    statusFromStream,
    positionsFromStream,
  } = selectControlRoomData({
    streamReady,
    streamSnapshot,
    fallbackStatus: statusQuery.data,
    fallbackPositions: positionsQuery.data,
  });
  const positionsState = positionsFromStream
    ? { ...positionsQuery, data: positions, hasMore: false, loadingMore: false }
    : positionsQuery;
  const signals = signalsQuery.data;
  const trades = tradesQuery.data;
  const logs = logsQuery.data;
  const perf = perfQuery.data;
  const accounts = Array.isArray(accountsQuery.data) ? accountsQuery.data : [];
  const allLogRecords = useMemo(
    () => ({
      signals,
      orders: ordersQuery.data,
      trades,
    }),
    [signals, ordersQuery.data, trades],
  );
  const pollQueries = [accountsQuery, statusQuery, positionsQuery, signalsQuery, tradesQuery, logsQuery, perfQuery, portfolioQuery, reportQuery, validationQuery, modelQuery];
  pollQueries.push(ordersQuery);
  const activePollQueries = pollQueries.filter((query) => !query.isPaused);
  const failedPanels = [
    ["status", statusQuery],
    ["accounts", accountsQuery],
    ["positions", positionsQuery],
    ["signals", signalsQuery],
    ["trades", tradesQuery],
    ["activity", logsQuery],
    ["performance", perfQuery],
    ["analytics", portfolioQuery],
    ["reports", reportQuery],
    ["validation", validationQuery],
    ["model", modelQuery],
    ["orders", ordersQuery],
  ]
    .filter(([name, query]) => !query.isPaused && query.error && !streamReady && !(name === "status" && streamSnapshot?.status))
    .map(([name]) => name);
  const hasError = failedPanels.length > 0;
  const hasFreshData = streamReady || activePollQueries.some((query) => query.lastSuccessAt);
  const backendUnavailable = Boolean(
    statusQuery.error && !statusQuery.lastSuccessAt && !streamSnapshot?.status
  );
  const staleData = Boolean(hasError && !backendUnavailable && hasFreshData);
  const banner = backendUnavailable
    ? { tone: "danger", title: "Backend unavailable", message: statusQuery.error }
    : staleData
      ? { tone: "warning", title: "Data sync degraded", message: `${failedPanels.join(", ")} ${failedPanels.length === 1 ? "panel is" : "panels are"} showing their last successful snapshot.` }
      : null;

  useEffect(() => {
    const timer = window.setTimeout(() => setSecondaryPollsReady(true), 350);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const syncRoute = () => setActiveView(routeFromLocation(window.location).view);
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, []);

  const navigate = useCallback((view) => {
    if (!isKnownView(view)) return;
    const nextPath = pathForView(view);
    if (window.location.pathname !== nextPath) {
      const url = new URL(window.location.href);
      url.pathname = nextPath;
      url.hash = "";
      window.history.pushState({ view }, "", url);
    }
    setActiveView(view);
  }, []);

  const [activeChartSymbol, setActiveChartSymbol] = useState(null);
  const handleOpenFullChart = useCallback((symbol) => {
    setActiveChartSymbol(symbol);
    navigate("chart");
  }, [navigate]);
  // This used to bump a `tick` counter that fed a `?t=` cache-buster onto the
  // status URL, which reset that poll to its blank initial state on every
  // click and re-rendered the whole tree. Re-run the active polls instead.
  const refresh = useCallback(() => {
    statusQuery.retry();
    positionsQuery.retry();
    signalsQuery.retry();
    tradesQuery.retry();
    logsQuery.retry();
    ordersQuery.retry();
    // The query objects themselves are new literals every render, so depending
    // on them would make this callback unstable and defeat the memoization.
    // Each `retry` is useCallback'd in lib/api.js, so these deps do hold.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statusQuery.retry, positionsQuery.retry, signalsQuery.retry,
    tradesQuery.retry, logsQuery.retry, ordersQuery.retry,
  ]);
  const refreshAccountPositions = useCallback(() => {
    statusQuery.retry();
    positionsQuery.retry();
    tradesQuery.retry();
    ordersQuery.retry();
    // Same reasoning as `refresh` above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusQuery.retry, positionsQuery.retry, tradesQuery.retry, ordersQuery.retry]);

  // Hoisted out of JSX: an inline arrow is a new prop value on every App render,
  // which would make React.memo on Sidebar/CouncilPulse a no-op.
  const toggleSidebar = useCallback(() => setSidebarCollapsed((value) => !value), []);
  const openBacktest = useCallback(() => setShowBacktest(true), []);
  const openSettings = useCallback(() => navigate("settings"), [navigate]);
  const openAiView = useCallback(() => navigate("ai"), [navigate]);

  const renderOverview = () => (
    <>
      <PageHeader
        eyebrow="CONTROL ROOM / OVERVIEW"
        title="Trading command center"
        subtitle="A calm, real-time view of execution, risk and market opportunity."
        action={<><button className="ghost-button" onClick={refresh}><Activity size={14} /> Refresh</button><button className="primary-button" onClick={() => setShowBacktest(true)}><FlaskConical size={14} /> Run backtest</button></>}
      />
      <StatStrip status={status} perf={perf} />
      <MissionStrip status={status} />
      <div className="dashboard-grid dashboard-grid-primary">
        <section className="panel panel-chart">
          <EquityChart selectedViewAccountId={selectedViewAccountId} status={status} />
        </section>
        <section className="panel panel-risk"><RiskPanel risk={status?.risk} account={status?.account} status={status} selectedViewAccountId={selectedViewAccountId} onRefresh={refresh} /></section>
        <section className="panel panel-scanner"><ScannerPanel scanner={status?.scanner} /></section>
      </div>
      <div className="dashboard-grid dashboard-grid-main">
        <section className="panel panel-table panel-wide"><PositionsTable positions={positions} listState={positionsState} selectedViewAccountId={selectedViewAccountId} onAction={refreshAccountPositions} onOpenFullChart={handleOpenFullChart} /></section>
        <section className="panel panel-feed"><SignalsFeed signals={signals} listState={signalsQuery} selectedViewAccountId={selectedViewAccountId} onRefresh={refresh} /></section>
      </div>
      <section className="panel panel-table panel-standalone mb-4"><OpenOrdersTable selectedViewAccountId={selectedViewAccountId} onOpenFullChart={handleOpenFullChart} /></section>
      <div className="dashboard-grid dashboard-grid-overview-lower">
        <section className="panel panel-wide"><TradesTable trades={trades} listState={tradesQuery} /></section>
        <section className="panel panel-council-pulse"><CouncilPulse ai={status?.ai} status={status} onOpen={openAiView} /></section>
      </div>
      <section className="panel panel-logs"><LogsPanel logs={logs} /></section>
      <section className="panel"><ExecutionPanel orders={ordersQuery.data} listState={ordersQuery} pending={status?.pending_orders} /></section>
      <section className="panel"><AlertsPanel /></section>
    </>
  );

  const renderChart = () => (
    <>
      <PageHeader
        eyebrow="MARKETS / CHART WORKSPACE"
        title="Live execution chart"
        subtitle="Candles, positions, protective levels, working orders and lock thresholds in one operator view."
        action={<button className="ghost-button" onClick={refresh}><Activity size={14} /> Refresh engine</button>}
      />
      <ChartWorkspace
        selectedViewAccountId={selectedViewAccountId}
        status={status}
        positions={positions}
        statusError={statusQuery.error}
        positionsError={positionsQuery.error}
        onRefresh={refresh}
        initialSymbol={activeChartSymbol}
      />
    </>
  );

  const renderPositions = () => (
    <>
      <PageHeader eyebrow="OPERATIONS / POSITIONS" title="Portfolio monitor" subtitle="Manage open exposure and review realized execution without leaving the control room." action={<button className="primary-button" onClick={() => navigate("settings")}><SlidersHorizontal size={14} /> Risk settings</button>} />
      <StatStrip status={status} perf={perf} />
      <section className="panel panel-table panel-standalone mb-4"><PositionsTable positions={positions} listState={positionsState} selectedViewAccountId={selectedViewAccountId} onAction={refreshAccountPositions} onOpenFullChart={handleOpenFullChart} /></section>
      <section className="panel panel-table panel-standalone mb-4"><OpenOrdersTable selectedViewAccountId={selectedViewAccountId} onOpenFullChart={handleOpenFullChart} /></section>
      <section className="panel panel-standalone"><TradesTable trades={trades} listState={tradesQuery} /></section>
    </>
  );


  const renderSignals = () => (
    <>
      <PageHeader eyebrow="MARKETS / SIGNALS" title="Signal intelligence" subtitle="Every candidate, confidence score and rejection reason in one transparent feed." action={<button className="ghost-button" onClick={refresh}><Radio size={14} /> Rescan view</button>} />
      <section className="panel panel-feed panel-standalone">
        <SignalsFeed signals={signals} listState={signalsQuery} selectedViewAccountId={selectedViewAccountId} onRefresh={refresh} />
      </section>
    </>
  );

  const renderNews = () => (
    <>
      <PageHeader eyebrow="INTELLIGENCE / ALL NEWS" title="Market news terminal" subtitle="Allowlisted macro, calendar and crypto context—organized for fast operator review." action={<button className="ghost-button" onClick={refresh}><Newspaper size={14} /> Refresh workspace</button>} />
      <section className="panel panel-news"><AllNewsPanel /></section>
    </>
  );

  const renderResearch = () => (
    <>
      <PageHeader eyebrow="RESEARCH / VALIDATION" title="Research lab" subtitle="Validate strategy behavior with realistic costs before promoting a configuration." action={<button className="primary-button" onClick={() => setShowBacktest(true)}><FlaskConical size={14} /> Open backtest lab</button>} />
      <div className="research-hero">
        <div className="research-hero-icon"><FlaskConical size={24} /></div>
        <div><span className="eyebrow">COST-AWARE SIMULATION</span><h3>Backtest before you deploy</h3><p>Fees, slippage, funding and risk-adjusted metrics are included in every replay.</p></div>
        <button className="primary-button" onClick={() => setShowBacktest(true)}>Configure replay <ChevronRight size={14} /></button>
      </div>
      <div className="dashboard-grid dashboard-grid-research"><section className="panel panel-chart"><EquityChart selectedViewAccountId={selectedViewAccountId} status={status} /></section><section className="panel panel-wide"><TradesTable trades={trades} listState={tradesQuery} /></section></div>
      <ResearchTelemetry analytics={portfolioQuery.data} report={reportQuery.data} validation={validationQuery.data} model={modelQuery.data} />
      <section className="research-modules">
        {[
          ["Portfolio heatmap", "Cross-symbol exposure and correlation", "risk telemetry"],
          ["Walk-forward optimizer", "Out-of-sample parameter validation", "research module"],
          ["Monte Carlo stress", "Distribution of possible equity paths", "risk telemetry"],
        ].map(([title, detail, tag]) => <div className="research-module" key={title}><div className="research-module-icon"><LineChart size={16} /></div><div><strong>{title}</strong><p>{detail}</p><span>{tag}</span></div></div>)}
      </section>
    </>
  );

  const renderAI = () => (
    <>
      <PageHeader eyebrow="INTELLIGENCE / AI COUNCIL" title="AI decision support" subtitle="Advisory signals are visible, auditable and always subordinate to the risk engine." action={<button className="ghost-button" onClick={() => navigate("settings")}><Settings2 size={14} /> AI settings</button>} />
      <section className="panel panel-ai panel-ai-workspace"><AIPanel ai={status?.ai} /></section>
      <div className="ai-safety-note"><ShieldCheck size={17} /><div><strong>Risk engine remains authoritative</strong><p>AI can score and explain approved signals, but cannot bypass sizing, exposure, cooldown or kill-switch gates.</p></div></div>
    </>
  );

  const renderActivity = () => (
    <>
      <PageHeader eyebrow="AUDIT / ACTIVITY" title="Execution timeline" subtitle="A low-noise operational log for orders, signals, risk events and service health." action={<><button className="ghost-button" onClick={refresh}><TerminalSquare size={14} /> Refresh logs</button><a className="ghost-button" href={`${API}/export/trades.csv`} download="atlas-trades.csv">Export trades</a><a className="ghost-button" href={`${API}/export/orders.csv`} download="atlas-orders.csv">Export orders</a></>} />
      <section className="panel panel-logs panel-logs-large"><LogsPanel logs={logs} listState={logsQuery} /></section>
      <section className="panel panel-standalone"><AlertsPanel /></section>
      <section className="panel panel-standalone"><TradesTable trades={trades} listState={tradesQuery} /></section>
    </>
  );

  const renderAllLogs = () => (
    <>
      <PageHeader
        eyebrow="SYSTEM / TERMINAL LOGS"
        title="All Process Logs & Live Terminal"
        subtitle="Real-time multi-channel console, process telemetry, security audit, and error filters."
        action={
          <button className="ghost-button" onClick={refresh}>
            <TerminalSquare size={14} /> Refresh stream
          </button>
        }
      />
      <div className="h-[750px] w-full">
        <AllLogsPanel
          logs={logs}
          listState={logsQuery}
          records={allLogRecords}
        />
      </div>
    </>
  );

  const renderBlockchain = () => (
    <>
      <PageHeader
        eyebrow="OBSERVABILITY / BLOCKCHAIN"
        title="On-chain telemetry"
        subtitle="Live public-chain health for assets in the selected account, with unsupported and stale sources shown explicitly."
        action={<button className="ghost-button" onClick={refresh}><Blocks size={14} /> Refresh telemetry</button>}
      />
      <section className="panel panel-standalone"><BlockchainMonitor selectedViewAccountId={selectedViewAccountId} /></section>
    </>
  );

  const renderBacktest = () => (
    <>
      <PageHeader
        eyebrow="STRATEGY / BACKTEST LAB"
        title="Backtest & Simulation Terminal"
        subtitle="Historical replay with modelled fees, slippage, walk-forward and Monte Carlo risk stress tests."
        action={
          <button className="ghost-button" onClick={refresh}>
            <Activity size={14} /> Refresh view
          </button>
        }
      />
      <BacktestLabPanel selectedViewAccountId={selectedViewAccountId} liveTrades={tradesQuery.data} />
    </>
  );

  const renderAgents = () => (
    <>
      <PageHeader
        eyebrow="ATLAS HYBRID AGENT FABRIC V3"
        title="Agent Fabric Command Center"
        subtitle="Live multi-agent intelligence, evidence-gated learning and non-executing safety authority across every registered agent."
        action={
          <button className="ghost-button" onClick={refresh}>
            <Activity size={14} /> Refresh telemetry
          </button>
        }
      />
      {/* Keyed on the account only. Including `tick` here remounted an 834-line
          panel on every refresh, discarding filter/search state and re-firing
          all seven of its polls — which already refresh on their own timers. */}
      <AgentFabricPanel
        key={`agent-fabric-${selectedViewAccountId}`}
        selectedViewAccountId={selectedViewAccountId}
      />
    </>
  );

  const renderView = () => {
    if (activeView === "chart") return renderChart();
    if (activeView === "positions") return renderPositions();
    if (activeView === "daily_pnl") return <DailyPnlPanel accountId={selectedViewAccountId} />;
    if (activeView === "signals") return renderSignals();
    if (activeView === "news") return renderNews();
    if (activeView === "agents") return renderAgents();
    if (activeView === "backtest") return renderBacktest();
    if (activeView === "research") return renderResearch();
    if (activeView === "ai") return renderAI();
    if (activeView === "activity") return renderActivity();
    if (activeView === "all_logs") return renderAllLogs();
    if (activeView === "blockchain") return renderBlockchain();
    if (activeView === "market_universe") return <MarketUniversePanel accountId={selectedViewAccountId} />;
    if (activeView === "settings") return (
      <SettingsPanel
        onOpenAI={() => navigate("ai")}
        onToast={() => refresh()}
        selectedViewAccountId={selectedViewAccountId}
        accountStatus={status}
      />
    );
    if (activeView === "not_found") {
      return (
        <div className="page-heading">
          <div>
            <p className="eyebrow">NAVIGATION / NOT FOUND</p>
            <h2>Workspace route not found</h2>
            <p className="page-subtitle">This dashboard route does not exist or is no longer available.</p>
          </div>
          <div className="page-heading-actions">
            <button className="primary-button" onClick={() => navigate("overview")}>
              <LayoutDashboard size={14} /> Return to overview
            </button>
          </div>
        </div>
      );
    }
    return renderOverview();
  };


  return (
    <AuthGate>
    <div className="app-frame" data-testid="dashboard">
      <Sidebar activeView={activeView} onNavigate={navigate} collapsed={sidebarCollapsed} onToggle={toggleSidebar} status={status} />
      <div className="app-main">
        <Header
          status={status}
          refresh={refresh}
          onBacktest={openBacktest}
          onSettings={openSettings}
          lastUpdated={statusFromStream ? controlRoomStream.lastEventAt : statusQuery.lastSuccessAt}
          accounts={accounts}
          accountsError={accountsQuery.error}
          selectedViewAccountId={selectedViewAccountId}
          onSelectViewAccount={handleSelectViewAccount}
        />
        {banner && (
          <div className={`health-banner health-banner-${banner.tone}`} data-testid="dashboard-health-banner">
            <span className="health-banner-title">{banner.title}</span><span>{banner.message}</span>
            <button className="ghost-button health-banner-retry" onClick={() => activePollQueries.filter((query) => query.error).forEach((query) => query.retry?.())}>
              Retry failed panels
            </button>
          </div>
        )}
        <main className="app-content">
          <ErrorBoundary key={`${activeView}-${selectedViewAccountId}`}>
            <Suspense fallback={<WorkspaceLoading />}>{renderView()}</Suspense>
          </ErrorBoundary>
        </main>
      </div>
      {showBacktest && (
        <Suspense fallback={null}>
          <BacktestModal onClose={() => setShowBacktest(false)} />
        </Suspense>
      )}
    </div>
    </AuthGate>
  );
}
